import React, { useEffect, useState } from 'react'
import Modal from 'components/modal'
import Button from 'components/button'
import Input from 'components/input'
import Select from 'components/select'
import api from 'helpers/api'
import { IOrganisation, IOrganisationUser } from 'types'
import { useApplicationStore } from 'hooks/application'
import { OrganisationRoles } from 'app-constants'

interface OrganisationEnforceLoginModalProps {
  open: boolean
  onClose: () => void
  organisation: IOrganisation
  onChanged?: (organisation: IOrganisation) => void
}

type Provider = 'null' | 'morta.io' | 'google.com' | 'microsoft.com' | 'saml'

const OrganisationEnforceLoginModal: React.FC<OrganisationEnforceLoginModalProps> = ({
  open,
  onClose,
  organisation,
  onChanged
}) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [saving, setSaving] = useState(false)
  const [provider, setProvider] = useState<Provider>('null')
  const [idpSsoUrl, setIdpSsoUrl] = useState<string>('')
  const [idpIssuer, setIdpIssuer] = useState<string>('')
  const [idpCertificate, setIdpCertificate] = useState<string>('')

  // Derive initial state from organisation when opened
  useEffect(() => {
    if (!open) return
    const enforced = organisation.enforceLogin
    if (!enforced) {
      setProvider('null')
    } else if (
      enforced === 'saml.morta.io' ||
      enforced === 'morta.io' ||
      enforced === 'google.com' ||
      enforced === 'microsoft.com'
    ) {
      setProvider(enforced as Provider)
    } else {
      // fallback (unknown string): treat as Any to avoid locking user out
      setProvider('null')
    }
    setIdpSsoUrl(organisation.idpSsoUrl || '')
    setIdpIssuer(organisation.idpIssuer || '')
    setIdpCertificate(organisation.idpCertificate || '')
  }, [open, organisation.enforceLogin, organisation.idpSsoUrl, organisation.idpCertificate, organisation.idpIssuer])

  // Keep users intact (email + role only)
  const usersPayload: IOrganisationUser[] = (organisation.users || [])
    .filter((u) => (u.user.email || '').trim() !== '')
    .map((u) => ({ user: { email: u.user.email }, role: u.role })) as unknown as IOrganisationUser[]

  const computedEnforceLogin = (): string | null => {
    if (provider === 'null') return null
    return provider
  }

  const handleSave = async () => {
    setSaving(true)

    const payload: IOrganisation = {
      ...organisation,
      enforceLogin: computedEnforceLogin(),
      idpSsoUrl: idpSsoUrl.trim() || null,
      idpIssuer: idpIssuer.trim() || null,
      idpCertificate: idpCertificate.trim() || null,
      users: usersPayload.filter((u) => u.role !== OrganisationRoles.MEMBER)
    }

    api
      .updateOrganisation(organisation.publicId, payload)
      .then((resp) => {
        setSnackbarMessage?.({ status: 'success', message: 'Login enforcement updated' })
        setSaving(false)
        onChanged?.(resp.data as IOrganisation)
        onClose()
      })
      .catch((e) => {
        displayErrorMessage(e)
        setSaving(false)
      })
  }

  const SP_ENTITY_ID = 'https://api.morta.io'
  const SP_ACS_URL = 'https://api.morta.io/v1/identities/saml/acs'
  // Single cert used for signing + encryption (from your existing metadata file)
  const X509_CERT_BASE64 =
    'MIIDKjCCAhICCQDMgvQun7FhNzANBgkqhkiG9w0BAQsFADBXMQswCQYDVQQGEwJVUzEOMAwGA1UECAwFU3RhdGUxDTALBgNVBAcMBENpdHkxFTATBgNVBAoMDE9yZ2FuaXphdGlvbjESMBAGA1UEAwwJbG9jYWxob3N0MB4XDTI1MDIyNjE5MzQ0OVoXDTI2MDIyNjE5MzQ0OVowVzELMAkGA1UEBhMCVVMxDjAMBgNVBAgMBVN0YXRlMQ0wCwYDVQQHDARDaXR5MRUwEwYDVQQKDAxPcmdhbml6YXRpb24xEjAQBgNVBAMMCWxvY2FsaG9zdDCCASIwDQYJKoZIhvcNAQEBBQADggEPADCCAQoCggEBAPIiqGuge91ap/TPS2dpsrg2a5KkZU/5x0+yZf1GgSVmrvYbrpy87MFr6gjDPfkz7aQgucvsNZSStvz6Vtcp0z8nWpRBdnviXW3UegRPA7FodjfK0mfo747M0sJFqcouqKe/i6Y/2LJEs/rDogAqVbr8tcSyRzt3Vk92IEzPlLbCB42WooyN1d9yvD3Qty84zoEiE7mAL/Qn7PvSPDcXDl6INS8jw3E6SS41Ed6Ca0mC+XQa5Ohq+bZNhp5WPDQPpa4hmH41hR96Bh0KEhZEIOmv/CxEemKNcNUUyw4KBSriUx9UUXlBbww30YS5vgVWAzDnzQG+NdBuN/PGO6UKw68CAwEAATANBgkqhkiG9w0BAQsFAAOCAQEAaJAWMMoJx6E1W/eVbZMgRMyr8kRqnrQPdqcPCjSDNlQm1aGvplnX62fbO8cBnQ3rG0dn/GrLp7HByUrSyfIv0fBFxP/MdqzELc0tYdfNJbArD8Z4qgHR2MxbGvaypvWVcO3/W4Lzif5uiAd/RBhDGPX7pblpS2XABN7oTRmHu18JHH94cV5fAYaaTiAaNEi40qPaP3693WZhj+qt7ADti5no9u/uE4jcGKbI72bvee2w8TD3UfsFO5D/srf2rFVG+CUQSutCbGfE2e19Gj6WR0H0C2ZzqS7f6S8gMC0rtLhytxRL620qAKUwP9VowpJj+jTXaPF7wGaoHQl1VDcDfg=='

  const downloadSpMetadata = () => {
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<EntityDescriptor xmlns="urn:oasis:names:tc:SAML:2.0:metadata"
                  xmlns:ds="http://www.w3.org/2000/09/xmldsig#"
                  entityID="${SP_ENTITY_ID}">
  <SPSSODescriptor protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol"
                   AuthnRequestsSigned="false"
                   WantAssertionsSigned="true">
    <KeyDescriptor use="signing">
      <ds:KeyInfo>
        <ds:X509Data>
          <ds:X509Certificate>${X509_CERT_BASE64}</ds:X509Certificate>
        </ds:X509Data>
      </ds:KeyInfo>
    </KeyDescriptor>
    <KeyDescriptor use="encryption">
      <ds:KeyInfo>
        <ds:X509Data>
          <ds:X509Certificate>${X509_CERT_BASE64}</ds:X509Certificate>
        </ds:X509Data>
      </ds:KeyInfo>
    </KeyDescriptor>
    <NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress</NameIDFormat>
    <AssertionConsumerService Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
                              Location="${SP_ACS_URL}"
                              index="0" isDefault="true"/>
  </SPSSODescriptor>
</EntityDescriptor>`
    const blob = new Blob([xml], { type: 'application/samlmetadata+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sp-metadata.xml'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const downloadSpCertificate = () => {
    const certPem = `-----BEGIN CERTIFICATE-----\n${X509_CERT_BASE64.match(/.{1,64}/g)?.join('\n')}\n-----END CERTIFICATE-----`
    const blob = new Blob([certPem], { type: 'application/x-pem-file;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sp-certificate.pem'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal id="organisation-enforce-login-modal" open={open} setOpen={onClose} title="Enforce Login Provider">
      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor="login-provider-enforcement" className="block text-sm text-muted mb-[5px]">
            Login Provider Enforcement
          </label>
          <Select
            optionsSelected={[provider]}
            options={[
              { label: 'Any', value: 'null' },
              { label: 'Email/Password', value: 'morta.io' },
              { label: 'Google', value: 'google.com' },
              { label: 'Microsoft', value: 'microsoft.com' },
              { label: 'Single Sign On', value: 'saml.morta.io' }
            ]}
            onOptionClick={(opt) => setProvider(opt as Provider)}
          />
        </div>

        <div>
          <label htmlFor="idp-sso-url" className="block text-sm text-muted mb-[5px]">
            Identity Provider Single Sign-On URL (IDP SSO URL)
          </label>
          <Input
            id="idp-sso-url"
            type="text"
            placeholder="https://your-idp.example.com/app/xxxx/sso/saml"
            value={idpSsoUrl}
            onChange={(e) => setIdpSsoUrl(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="idp-issuer" className="block text-sm text-muted mb-[5px]">
            Identity Provider Issuer
          </label>
          <Input
            id="idp-issuer"
            type="text"
            value={idpIssuer}
            placeholder="https://www.your-idp.example.com/YYY"
            onChange={(e) => setIdpIssuer(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="idp-certificate" className="block text-sm text-muted mb-[5px]">
            IDP SSO Certificate (X.509 Certificate)
          </label>
          <textarea
            id="idp-certificate"
            className="w-full"
            rows={10}
            placeholder="e.g. -----BEGIN CERTIFICATE-----
                  MIIDqDCCApCgAwIBAgIGAZfy6thVMA0GCSqGSIb3DQEBCwUAMIGUMQswCQYDVQQGEwJVUzETMBEG
                  A1UECAwKQ2FsaWZvcm5pYTEWMBQGA1UEBwwNU2FuIEZyYW5jaXNjbzENMAsGA1UECgwET2t0YTEU
                  MBIGA1UECwwLU1NPUHJvdmlkZXIxFTATBgNVBAMMDGRldi04Mzk5MDgzOTEcMBoGCSqGSIb3DQEJ
                  ARYNaW5mb0Bva3RhLmNvbTAeFw0yNTA3MTAwNTU4MjZaFw0zNTA3MTAwNTU5MjZaMIGUMQswCQYD
                  VQQGEwJVUzETMBEGA1UECAwKQ2FsaWZvcm5pYTEWMBQGA1UEBwwNU2FuIEZyYW5jaXNjbzENMAsG
                  A1UECgwET2t0YTEUMBIGA1UECwwLU1NPUHJvdmlkZXIxFTATBgNVBAMMDGRldi04Mzk5MDgzOTEc
                  MBoGCSqGSIb3DQEJARYNaW5mb0Bva3RhLmNvbTCCASIwDQYJKoZIhvcNAQEBBQADggEPADCCAQoC
                  ggEBANRc1I9F309xgsHXPtGQv/mNx/+uPhKCl81ea7vzFpLWm2oI7EYyULBfK/dW0CRYtuyMHWGZ
                  QsLJUF5hQyfW5Gz0DZ1rk1I5JOmqy1bFyJ9CXt0iacyZmbNN1jExfdqDK/5VAdm5F/v6+Apm5PO1
                  hoEqHXmjAnAZGAc3hjmzX7mrfJXKECG3xigqh9pxeCFqqQ5KdALofO0qn9PMKYWvEv/JpH7IcwHb
                  m7JEVOx/qxCO4QUBkO062WGP2oZ0FpS9CLuuR3Ggh4NyxumqNsP6PPoHuy7DEY8EuUt9GyZvXM2E
                  5IW+YF3h+gon9CDMxhAMfh7isyMxmmiVTrOyHrdVcPsCAwEAATANBgkqhkiG9w0BAQsFAAOCAQEA
                  KpPuk6yxGAwAVLCdbTFPThVaOgTRjZimeQOWr/1k7HOQBM58pzlKjAXEboktLa0QI+FKNODAQ5XB
                  tK5UcyRLxJkLSbpaLHI9JmliOJ9Ael342Eg429uKIZvl+XwyRAUQK0OqaSljQsU/7tHtmjoiG9td
                  NtI4uU2uU744CPTFiS1wNcihlLqidcLDTt+3BYW2claB7JFxzc0RdH/dWygW27jJwPmXgJejRXPR
                  I/uGi+qSXCl4/Td7Bw4lUCWomfmKV8zwuxw2xKzRglfkStKrhozpD/yOATpLWaSBAJr4RKt5W9TQ
                  dVNPb/ePOYsgXara4nDkWCawPSuxfn7nP5yilQ==
                  -----END CERTIFICATE-----"
            value={idpCertificate}
            onChange={(e) => setIdpCertificate(e.target.value)}
          />

          <div className="rounded border border-divider-subtle bg-surface-muted p-4">
            <div className="text-sm font-semibold mb-[8px]">How to configure SAML (IdP)</div>
            <ol className="text-sm pl-4 mb-3">
              <li>In your IdP, create a new SAML 2.0 application/integration.</li>
              <li>
                Set <strong>Entity ID (Audience URI)</strong> to <code>{SP_ENTITY_ID}</code>.
              </li>
              <li>
                Set <strong>ACS (Assertion Consumer Service) URL</strong> to <code>{SP_ACS_URL}</code>.
              </li>
              <li>
                Set <strong>NameID format</strong> to <code>emailAddress</code>.
              </li>
              <li>Upload our SP certificate (included in the metadata) and enable SHA-256 if prompted.</li>
              <li>Assign the users/groups who should be able to log in.</li>
            </ol>

            <div className="flex items-center gap-2">
              <Button internalType="outline" onClick={downloadSpCertificate}>
                Download SP Certificate (PEM)
              </Button>
              <Button internalType="outline" onClick={downloadSpMetadata}>
                Download SP Metadata (XML)
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center">
          <Button className="mr-2" internalType="danger" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={saving}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default OrganisationEnforceLoginModal
